import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tab, Block, DesignSystem, Plugin } from "../../types/ThemeTypes";
import { BaseBlockProps } from "../../types/BlockTypes";
import { BLOCK_COMPONENTS } from "../../constants/blockComponents";
import landingPageStyles from "@/styles/landing-layout.module.css";

interface BodyProps {
  tabs: Tab[];
  designSystem: DesignSystem;
  plugins: Plugin[];
  tenantDomain?: string;
  apiVersion?: string;
}

// Type-safe block component interface (simplified, no CMS props)
interface BlockComponentProps extends BaseBlockProps {
  designSystem?: DesignSystem;
  plugins?: Plugin[];
  tenantDomain?: string;
  apiVersion?: string;
  currentTabIndex?: number;
}

// Type-safe block component factory with memoization
const createTypeSafeBlockComponent = (() => {
  const componentCache = new Map<
    string,
    React.ComponentType<BlockComponentProps>
  >();

  return (block: Block) => {
    const BlockComponent =
      BLOCK_COMPONENTS[block.key as keyof typeof BLOCK_COMPONENTS];

    if (!BlockComponent) return null;

    const cacheKey = block.key;

    if (componentCache.has(cacheKey)) {
      return componentCache.get(cacheKey)!;
    }

    const TypeSafeBlockComponent = React.memo((props: BlockComponentProps) => {
      return (
        <BlockComponent {...(props as unknown as Record<string, unknown>)} />
      );
    });

    componentCache.set(cacheKey, TypeSafeBlockComponent);
    return TypeSafeBlockComponent;
  };
})();

// Simple utility to get valid tab index (without CMS dependencies)
const getValidTabIndex = (tabs: Tab[], currentIndex: number): number => {
  if (!tabs || tabs.length === 0) return -1;

  // Filter enabled and visible tabs
  const enabledTabs = tabs.filter(
    (tab) => tab.enabled !== false && !tab.hidden
  );

  if (enabledTabs.length === 0) return -1;

  // Find the tab in the original array
  if (
    tabs[currentIndex] &&
    tabs[currentIndex].enabled !== false &&
    !tabs[currentIndex].hidden
  ) {
    return currentIndex;
  }

  // Find first enabled tab
  const firstEnabledIndex = tabs.findIndex(
    (tab) => tab.enabled !== false && !tab.hidden
  );
  return firstEnabledIndex >= 0 ? firstEnabledIndex : 0;
};

const Body: React.FC<BodyProps> = ({
  tabs,
  designSystem,
  plugins,
  tenantDomain,
  apiVersion,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  // Filter enabled tabs
  const enabledTabs = useMemo(() => {
    return tabs.filter((tab) => tab.enabled !== false && !tab.hidden);
  }, [tabs]);

  // Update active tab index when tabs change
  useEffect(() => {
    if (enabledTabs.length > 0) {
      const validIndex = getValidTabIndex(tabs, activeTabIndex);
      if (validIndex >= 0 && validIndex !== activeTabIndex) {
        setActiveTabIndex(validIndex);
      } else if (activeTabIndex >= tabs.length) {
        setActiveTabIndex(0);
      }
    }
  }, [tabs, enabledTabs.length, activeTabIndex]);

  const renderBlock = useCallback(
    (block: Block, index: number, currentTabIndex: number) => {
      // Check isActive: can be number (1 = active) or boolean (false = inactive)
      const isBlockActive = block?.isActive !== false && block?.isActive !== 0;
      if (!isBlockActive || block?.properties?.enabled === false) {
        return null;
      }

      const TypeSafeBlockComponent = createTypeSafeBlockComponent(block);
      if (!TypeSafeBlockComponent) {
        return null;
      }

      return (
        <React.Fragment key={`body-${currentTabIndex}-${index}`}>
          <TypeSafeBlockComponent
            title={block.name}
            enabled={true}
            properties={block.properties as unknown}
            designSystem={designSystem}
            plugins={plugins}
            tenantDomain={tenantDomain}
            apiVersion={apiVersion}
            currentTabIndex={currentTabIndex}
          />
        </React.Fragment>
      );
    },
    [designSystem, plugins, tenantDomain, apiVersion]
  );

  const renderTabContent = useCallback(
    (tabs: Tab[], activeTabIndex: number) => {
      const tab = tabs[activeTabIndex];
      if (!tab?.blocks || tab.blocks.length === 0) {
        return (
          <div className={landingPageStyles.landing_page_box}>
            <p>No content available</p>
          </div>
        );
      }

      return (
        <div className={landingPageStyles.landing_page_blocks_wrapper}>
          {tab.blocks.map((block, index) =>
            renderBlock(block, index, activeTabIndex)
          )}
        </div>
      );
    },
    [renderBlock]
  );

  if (!tabs || tabs.length === 0 || enabledTabs.length === 0) {
    return (
      <div className={landingPageStyles.landing_page_box}>
        <p>No content available</p>
      </div>
    );
  }

  const validTabIndex = getValidTabIndex(tabs, activeTabIndex);
  const activeTab = tabs[validTabIndex];

  return (
    <div className={landingPageStyles.landing_page_tabs_container}>
      {/* Tab Navigation */}
      {enabledTabs.length > 1 && (
        <div className={landingPageStyles.landing_page_tabs_navigation}>
          <div className={landingPageStyles.landing_page_tabs_scrollable}>
            {tabs.map((tab, index) => {
              if (tab.enabled === false || tab.hidden) return null;

              const isActive = index === validTabIndex;
              const tabClassName = isActive
                ? `${landingPageStyles.landing_page_tab_button} ${landingPageStyles.landing_page_tab_button_active}`
                : landingPageStyles.landing_page_tab_button;

              return (
                <button
                  key={`tab-${index}`}
                  className={tabClassName}
                  onClick={() => setActiveTabIndex(index)}
                >
                  {tab.tabName || `Tab ${index + 1}`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className={landingPageStyles.landing_page_tab_content}>
        {activeTab && renderTabContent(tabs, validTabIndex)}
      </div>
    </div>
  );
};

export default Body;
